/**
 * useKapeJob — reusable durable-job hook for the KAPE Course Editor.
 *
 * Handles: start / restore / poll / retry lifecycle for any KAPE
 * async job type (lesson, slides, narration_script, narration_audio,
 * workbook, final_exam, bulk_lessons).
 *
 * The hook is stateless w.r.t. the DB — jobs live on the backend. This
 * hook merely reflects the newest job for `(courseId, jobType, targetId)`
 * on mount and every poll cycle. Refresh / tab-close does not cancel
 * the job; the effect on remount picks up wherever it left off.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TERMINAL = new Set(["completed", "failed", "cancelled", "stale"]);
const ACTIVE = new Set(["queued", "running", "retrying"]);

export function useKapeJob({ courseId, jobType, targetId, onComplete }) {
  const [job, setJob] = useState(null);
  const stopped = useRef(false);
  const inflight = useRef(false);

  const targetPath = targetId || "-";

  // Restore in-flight job state on mount / when target changes.
  useEffect(() => {
    stopped.current = false;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(
          `/kcis/admin/kape/generation-jobs/latest/${courseId}/${jobType}/${targetPath}`
        );
        if (!alive || !data?.job) return;
        setJob(data.job);
        if (ACTIVE.has(data.job.status)) startPolling(data.job.id);
      } catch { /* ignore */ }
    })();
    return () => {
      alive = false;
      stopped.current = true;
    };
  }, [courseId, jobType, targetPath]);

  const startPolling = useCallback((jobId) => {
    if (inflight.current) return;
    inflight.current = true;
    (async () => {
      for (let i = 0; i < 720; i++) {  // up to 60 min at 5s intervals
        if (stopped.current) break;
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const { data } = await api.get(`/kcis/admin/kape/generation-jobs/${jobId}`);
          setJob(data.job);
          if (TERMINAL.has(data.job.status)) {
            if (data.job.status === "completed" && onComplete) {
              try { await onComplete(data.job); } catch { /* noop */ }
            }
            break;
          }
        } catch { /* keep polling on transient errors */ }
      }
      inflight.current = false;
    })();
  }, [onComplete]);

  const start = useCallback(async (endpointPath, extraQuery = "") => {
    // endpointPath: e.g. "/slides", "/final-exam"
    // extraQuery: additional url-encoded params starting with "&" if needed
    setJob({ id: "", status: "queued", current_step: "starting", progress_percent: 0 });
    try {
      const targetQuery = targetId ? `&${jobType === "workbook" ? "module_id" : "lesson_id"}=${targetId}` : "";
      const url = `/kcis/admin/kape/generation-jobs${endpointPath}?course_id=${courseId}${targetQuery}${extraQuery}`;
      const { data } = await api.post(url);
      setJob({ id: data.job_id, status: data.status, current_step: "queued", progress_percent: 0 });
      startPolling(data.job_id);
      return data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not queue job");
      setJob(null);
      throw err;
    }
  }, [courseId, jobType, targetId, startPolling]);

  const retry = useCallback(async () => {
    if (!job?.id) return;
    try {
      await api.post(`/kcis/admin/kape/generation-jobs/${job.id}/retry`);
      startPolling(job.id);
    } catch { toast.error("Retry failed"); }
  }, [job, startPolling]);

  return { job, start, retry, isActive: !!job && ACTIVE.has(job.status),
           isFailed: !!job && (job.status === "failed" || job.status === "stale"),
           isComplete: !!job && job.status === "completed" };
}


// Bulk-lessons variant — targetId is empty; endpoint is `/bulk-lessons`
// with only course_id.
export function useKapeBulkLessonsJob({ courseId, onComplete }) {
  return useKapeJob({ courseId, jobType: "bulk_lessons", targetId: "", onComplete });
}


// Final-exam variant — targetId empty, needs num_questions query param.
export function useKapeFinalExamJob({ courseId, onComplete }) {
  const h = useKapeJob({ courseId, jobType: "final_exam", targetId: "", onComplete });
  const start = useCallback((numQuestions = 20) => {
    return h.start("/final-exam", `&num_questions=${numQuestions}`);
  }, [h]);
  return { ...h, start };
}


// Convenience factories for lesson-scoped jobs.
export function useKapeSlidesJob({ courseId, lessonId, onComplete }) {
  const h = useKapeJob({ courseId, jobType: "slides", targetId: lessonId, onComplete });
  return { ...h, start: () => h.start("/slides") };
}

export function useKapeNarrationScriptJob({ courseId, lessonId, onComplete }) {
  const h = useKapeJob({ courseId, jobType: "narration_script", targetId: lessonId, onComplete });
  return { ...h, start: () => h.start("/narration-script") };
}

export function useKapeNarrationAudioJob({ courseId, lessonId, onComplete }) {
  const h = useKapeJob({ courseId, jobType: "narration_audio", targetId: lessonId, onComplete });
  return { ...h, start: () => h.start("/narration-audio") };
}

export function useKapeWorkbookJob({ courseId, moduleId, onComplete }) {
  const h = useKapeJob({ courseId, jobType: "workbook", targetId: moduleId, onComplete });
  return { ...h, start: () => h.start("/workbook") };
}

export function useKapeImagePopulateJob({ courseId, lessonId, onComplete }) {
  const h = useKapeJob({ courseId, jobType: "image_populate", targetId: lessonId, onComplete });
  return { ...h, start: () => h.start("/image-populate") };
}

export function useKapeNarrationMigrationJob({ courseId, onComplete }) {
  const h = useKapeJob({ courseId, jobType: "narration_migration", targetId: "", onComplete });
  const start = useCallback(async (lessonIds) => {
    const url = `/kcis/admin/kape/generation-jobs/narration-migration?course_id=${courseId}`;
    try {
      const { data } = await api.post(url, { lesson_ids: lessonIds, confirm: true });
      return data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start migration");
      throw err;
    }
  }, [courseId]);
  return { ...h, start };
}


// Helper to format a compact status pill.
export function jobStatusLabel(job) {
  if (!job) return "";
  if (job.status === "queued") return "Queued";
  if (job.status === "running" || job.status === "retrying") {
    const pct = job.progress_percent ? `${job.progress_percent}%` : "";
    return `${job.current_step || "Running"} ${pct}`.trim();
  }
  if (job.status === "completed") return "Complete";
  if (job.status === "failed" || job.status === "stale") {
    return `Failed · ref ${job.id?.slice(0, 8) || "—"}`;
  }
  return job.status;
}
